/** @jsxImportSource @emotion/react */
import React, { useEffect } from "react";
import { css } from "@emotion/react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import i18n from "i18next";

import { setLanguage } from "../../../redux/actions/language";
import { get_about_us } from "../../../redux/actions/about_us/get_about_us";
import { get_banners } from "../../../redux/actions/banners/get_banners";
import { get_faqs } from "../../../redux/actions/faqs/get_faqs";
import { get_info_cards } from "../../../redux/actions/info_cards/get_info_cards";
import { get_list_items } from "../../../redux/actions/list_items/get_list_items";
import { get_our_work } from "../../../redux/actions/our_work/get_our_work";
import { get_portofolio } from "../../../redux/actions/portofolio/get_portofolio";
import { get_staff_cards } from "../../../redux/actions/staff_cards/get_staff_cards";
import { get_staff } from "../../../redux/actions/staff/get_staff";
import { get_enterprises } from "../../../redux/actions/enterprises/get_enterprises";
import { get_founders } from "../../../redux/actions/founders/get_founders";
import { get_how_to_apply } from "../../../redux/actions/how_to_apply/get_how_to_apply";
import { get_info_apply } from "../../../redux/actions/info_apply/get_info_apply";
import { get_info_talent } from "../../../redux/actions/info_talent/get_info_talent";
import { get_card_apply } from "../../../redux/actions/card_apply/get_card_apply";

const page = css`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const hero = css`
  background: linear-gradient(135deg, #001740 0%, #253b7f 100%);
  color: white;
  border-radius: 24px;
  padding: 32px;
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.14);
`;

const eyebrow = css`
  font-size: 13px;
  opacity: 0.78;
  margin-bottom: 8px;
`;

const title = css`
  margin: 0;
  font-size: 30px;
  font-weight: 800;
`;

const subtitle = css`
  margin: 10px 0 0;
  max-width: 720px;
  opacity: 0.82;
  line-height: 1.55;
`;

const grid = css`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const card = css`
  background: white;
  border: 1px solid #eef0f5;
  border-radius: 20px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.18s ease;
  box-shadow: 0 10px 26px rgba(15, 23, 42, 0.04);

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 18px 38px rgba(15, 23, 42, 0.08);
    border-color: #dbeafe;
  }
`;

const cardLabel = css`
  color: #64748b;
  font-size: 14px;
  margin-bottom: 10px;
`;

const cardValue = css`
  color: #001740;
  font-size: 34px;
  font-weight: 800;
`;

const cardHint = css`
  color: #94a3b8;
  font-size: 13px;
  margin-top: 8px;
`;

const sectionTitle = css`
  margin: 8px 0 0;
  color: #001740;
  font-size: 20px;
  font-weight: 800;
`;

const quickGrid = css`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const quickCard = css`
  background: white;
  border: 1px solid #eef0f5;
  border-radius: 18px;
  padding: 18px;
  cursor: pointer;
  transition: all 0.18s ease;

  &:hover {
    background: #f8fbff;
    border-color: #dbeafe;
  }
`;

const quickTitle = css`
  color: #001740;
  font-weight: 800;
  margin-bottom: 6px;
`;

const quickDescription = css`
  color: #64748b;
  font-size: 14px;
  line-height: 1.45;
`;

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const token = useSelector((state: any) => state.login.access);
  const lang = useSelector((state: any) => state.language.language);

  const banners = useSelector((state: any) => state.get_banners.data);
  const infoCards = useSelector((state: any) => state.get_info_cards.data);
  const enterprises = useSelector((state: any) => state.get_enterprises.data);
  const founders = useSelector((state: any) => state.get_founders.data);
  const faqs = useSelector((state: any) => state.get_faqs.data);
  const staffCards = useSelector((state: any) => state.get_staff_cards.data);
  const aboutUs = useSelector((state: any) => state.get_about_us.data);
  const infoApply = useSelector((state: any) => state.get_info_apply.data);

  const count = (resource: any) => resource?.data?.length || 0;

  useEffect(() => {
    if (!lang) {
      i18n.changeLanguage("es");
      dispatch(setLanguage("es"));
    }

    dispatch(get_about_us({ lang: null, token }));
    dispatch(get_banners({ lang: null, token }));
    dispatch(get_faqs({ lang: null, token }));
    dispatch(get_info_cards({ lang: null, token }));
    dispatch(get_list_items({ lang: null, token }));
    dispatch(get_our_work({ lang: null, token }));
    dispatch(get_portofolio({ lang: null, token }));
    dispatch(get_staff_cards({ lang: null, token }));
    dispatch(get_staff({ lang: null, token }));
    dispatch(get_enterprises({ lang: null, token }));
    dispatch(get_founders({ lang: null, token }));
    dispatch(get_how_to_apply({ lang: null, token }));
    dispatch(get_info_apply({ lang: null, token }));
    dispatch(get_info_talent({ lang: null, token }));
    dispatch(get_card_apply({ lang: null, token }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const metrics = [
    {
      label: "Banners",
      value: count(banners),
      path: "/app/content-management/manage-banners",
    },
    {
      label: "Secciones",
      value: count(infoCards),
      path: "/app/content-management/manage-info-cards",
    },
    {
      label: "Empresas",
      value: count(enterprises),
      path: "/app/content-management/manage-enterprises",
    },
    {
      label: "Fundadores",
      value: count(founders),
      path: "/app/content-management/manage-founders",
    },
    {
      label: "FAQs",
      value: count(faqs),
      path: "/app/content-management/manage-faqs",
    },
    {
      label: "Staff",
      value: count(staffCards),
      path: "/app/content-management/manage-staff-team",
    },
    {
      label: "Quiénes somos",
      value: count(aboutUs),
      path: "/app/content-management/manage-about-us",
    },
    {
      label: "Quiero aplicar",
      value: count(infoApply),
      path: "/app/content-management/manage-info-apply",
    },
  ];

  const quickActions = [
    {
      title: "Editar banner principal",
      description: "Actualiza el mensaje principal y la imagen de portada.",
      path: "/app/content-management/manage-banners",
    },
    {
      title: "Gestionar portafolio",
      description: "Administra empresas, fundadores y casos visibles.",
      path: "/app/content-management/manage-enterprises",
    },
    {
      title: "Actualizar preguntas frecuentes",
      description: "Mantén claro el contenido para startups e inversionistas.",
      path: "/app/content-management/manage-faqs",
    },
  ];

  return (
    <div css={page}>
      <section css={hero}>
        <div css={eyebrow}>Escalatec CMS</div>
        <h1 css={title}>Panel de administración</h1>
        <p css={subtitle}>
          Gestiona el contenido principal de la web, revisa secciones activas y
          accede rápidamente a los módulos más importantes.
        </p>
      </section>

      <h2 css={sectionTitle}>Resumen de contenido</h2>

      <section css={grid}>
        {metrics.map((item) => (
          <article key={item.label} css={card} onClick={() => navigate(item.path)}>
            <div css={cardLabel}>{item.label}</div>
            <div css={cardValue}>{item.value}</div>
            <div css={cardHint}>Ver módulo →</div>
          </article>
        ))}
      </section>

      <h2 css={sectionTitle}>Accesos rápidos</h2>

      <section css={quickGrid}>
        {quickActions.map((item) => (
          <article
            key={item.title}
            css={quickCard}
            onClick={() => navigate(item.path)}
          >
            <div css={quickTitle}>{item.title}</div>
            <div css={quickDescription}>{item.description}</div>
          </article>
        ))}
      </section>
    </div>
  );
};

export default Dashboard;